<template>
  <v-card flat>
    <v-col cols="12" class="px-0">
      <v-btn color="primary" icon @click="beforeLeave">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        Smart Autoplay
      </span>
    </v-col>

    <v-row no-gutters class="py-0 px-2">
      <span>
        {{ $t('message.pages.video.messageAutoplay') }}
      </span>
      <v-col cols="12" class="mt-2">
        <v-row>
          <!-- autoplay -->
          <v-col cols="12">
            <v-switch
              v-model="autoplay"
              class="mt-2"
              label="Autoplay"
              hide-details
              :disabled="!hasEditVideoPerm"
              @click="changePlayer({ forceReset: true })"
            />
          </v-col>

          <!-- smart autoplay -->
          <v-col cols="12" class="pt-1">
            <v-tooltip bottom :disabled="autoplay">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    v-model="smartAutoplay"
                    class="mt-0"
                    hide-details
                    :label="$t('message.pages.videoEditModal.smartAutoplay')"
                    :disabled="!autoplay || !hasEditVideoPerm"
                    @click="changePlayer"
                  />
                </div>
              </template>
              <span>{{
                $t('message.pages.videoEditModal.smartAutoplayMessage')
              }}</span>
            </v-tooltip>
          </v-col>

          <!-- small muted indicator -->
          <v-col v-if="autoplay && !mutedIndicatorIcon" cols="12" class="pt-1">
            <v-switch
              v-model="smallMutedIcon"
              class="mt-0"
              :label="$t('message.pages.videoEditModal.smallMutedIcon')"
              hide-details
              :disabled="!autoplay || !hasEditVideoPerm"
              @click="changePlayer"
            />
          </v-col>

          <!-- personalize muted indicator -->
          <v-col cols="12" class="pt-1">
            <v-tooltip bottom :disabled="autoplay">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    v-model="mutedIndicatorIcon"
                    class="mt-0"
                    hide-details
                    :label="$t('message.pages.videoEditModal.mutedIndicator')"
                    :disabled="!autoplay || !hasEditVideoPerm"
                    @click="changePlayer"
                  />
                </div>
              </template>
              <span>{{
                $t('message.pages.videoEditModal.smartAutoplayMessage')
              }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
      <v-scroll-x-transition mode="out-in" hide-in-live>
        <v-col v-if="mutedIndicatorIcon" cols="12" class="mt-8 mb-4">
          <v-row>
            <v-col cols="12">
              <v-tabs
                :value="mutedIndicatorAnimation === 'custom' ? 1 : 0"
                @change="changePlayer"
              >
                <v-tab @click="handleTabDefault">
                  {{ $t('message.pages.video.mutedIndicator.tabDefault') }}
                </v-tab>
                <v-tab @click="handleTabCustom">
                  <span class="ml-1 mr-3">
                    {{ $t('message.pages.video.mutedIndicator.tabCustom') }}
                  </span>
                  <sup class="newItemText">{{ $t('message.newItem') }}</sup>
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>

          <v-row v-if="mutedIndicatorAnimation !== 'custom'">
            <!-- animation -->
            <v-col cols="6" class="pt-2 mb-2">
              <v-select
                v-model="mutedIndicatorAnimation"
                :items="svgAnimationTypes"
                :menu-props="{ top: false, y: true }"
                item-text="name"
                item-value="value"
                :label="$t('message.pages.video.mutedIndicator.animations')"
                outlined
                dense
                hide-details
                :disabled="!hasEditVideoPerm"
                @change="changePlayer"
              />
            </v-col>

            <!-- mutedIndicator::font size -->
            <v-col cols="6" class="pt-2 mb-2">
              <v-slider
                v-model="mutedIndicatorFontSize"
                class="mt-0"
                :label="
                  $t(
                    'message.pages.video.mutedIndicator.mutedIndicatorFontSize'
                  ) + ':'
                "
                color="primary"
                thumb-color="primary"
                thumb-label="always"
                :max="32"
                :min="12"
                hide-details
                @change="changePlayer"
              >
                <template v-slot:thumb-label>
                  {{ mutedIndicatorFontSize }}px
                </template>
              </v-slider>
            </v-col>

            <!-- animation::impact--loop -->
            <template v-if="mutedIndicatorAnimation === 'impact'">
              <v-col cols="6" class="pt-2 mb-2">
                <v-switch
                  v-model="mutedIndicatorLoop"
                  class="pa-0 ma-0 mt-2"
                  :label="
                    $t('message.pages.video.mutedIndicator.animationInLoop')
                  "
                  hide-details
                  @click="changePlayer"
                />
              </v-col>
              <v-col cols="6" class="pt-2 mb-2">
                <v-text-field
                  v-model="mutedIndicatorLoopDuration"
                  :disabled="!mutedIndicatorLoop"
                  :rules="[animation.max, animation.min]"
                  :placeholder="
                    $t('message.pages.video.mutedIndicator.inSeconds')
                  "
                  color="primary"
                  :label="
                    $t('message.pages.video.mutedIndicator.animationLoopTime')
                  "
                  maxlength="3"
                  dense
                  outlined
                  hide-details
                  @input="changePlayer"
                  @keypress="numberOnly"
                />
              </v-col>
            </template>

            <!-- mutedIndicator::button size -->
            <v-col cols="6" class="pt-2 mb-2">
              <v-select
                v-model="mutedIndicatorSize"
                :label="
                  $t('message.pages.video.mutedIndicator.mutedIndicatorSize')
                "
                :items="mutedIndicatorSizeTypes"
                :menu-props="{ top: false, y: true }"
                item-text="name"
                item-value="value"
                outlined
                dense
                hide-details
                @change="changePlayer"
              />
            </v-col>

            <!-- mutedIndicator::border size -->
            <v-col cols="6" class="pt-2 mb-2">
              <v-slider
                v-model="mutedIndicatorBorderSize"
                class="mt-2"
                :label="
                  $t(
                    'message.pages.video.mutedIndicator.mutedIndicatorBorderSize'
                  ) + ':'
                "
                color="primary"
                thumb-color="primary"
                thumb-label="always"
                :max="10"
                :min="0"
                hide-details
                @change="changePlayer"
              >
                <template v-slot:thumb-label>
                  {{ mutedIndicatorBorderSize }}px
                </template>
              </v-slider>
            </v-col>

            <!-- text::top -->
            <v-col cols="12" class="pt-2 mb-2">
              <v-text-field
                v-model="mutedIndicatorTextTop"
                :placeholder="
                  $t(
                    'message.pages.video.mutedIndicator.mutedIndicatorTextPlaceholder'
                  )
                "
                :label="$t('message.pages.video.mutedIndicator.topText')"
                :rules="[rules.maxLength]"
                color="primary"
                dense
                outlined
                hide-details
                @blur="changePlayer"
              />
            </v-col>

            <!-- text::bottom -->
            <v-col cols="12" class="pt-2 mb-2">
              <v-text-field
                v-model="mutedIndicatorTextBottom"
                :placeholder="
                  $t(
                    'message.pages.video.mutedIndicator.mutedIndicatorTextPlaceholder'
                  )
                "
                :label="$t('message.pages.video.mutedIndicator.bottomText')"
                :rules="[rules.maxLength]"
                color="primary"
                dense
                outlined
                hide-details
                @blur="changePlayer"
              />
            </v-col>

            <!-- color: foreground -->
            <v-col cols="6" class="pt-2 mb-2">
              <v-row no-gutters align="center">
                <v-menu
                  offset-x
                  v-model="mutedFontColor"
                  top
                  nudge-bottom="105"
                  nudge-left="16"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ml-2"
                      :color="mutedIndicatorTextColor"
                      v-on="on"
                      :style="setMutedFontColor"
                      icon
                      elevation="3"
                    ></v-btn>
                  </template>
                  <v-color-picker
                    elevation="3"
                    v-model="mutedIndicatorTextColor"
                    flat
                    mode="hexa"
                    hide-mode-switch
                    :maxlength="7"
                    @input="changePlayer"
                  ></v-color-picker>
                </v-menu>
                <span class="ml-2">{{
                  $t('message.pages.video.mutedIndicator.fgColor')
                }}</span>
              </v-row>
            </v-col>

            <!-- color: background -->
            <v-col cols="6" class="pt-2 mb-2">
              <v-row no-gutters align="center">
                <v-menu
                  offset-x
                  v-model="mutedBackgroundColor"
                  top
                  nudge-bottom="105"
                  nudge-left="16"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ml-2"
                      :color="mutedIndicatorBackgroundColor"
                      v-on="on"
                      :style="setMutedIndicatorBackgroundColor"
                      icon
                      elevation="3"
                    ></v-btn>
                  </template>
                  <v-color-picker
                    elevation="3"
                    v-model="mutedIndicatorBackgroundColor"
                    flat
                    mode="hexa"
                    hide-mode-switch
                    :maxlength="7"
                    @input="changePlayer"
                  ></v-color-picker>
                </v-menu>
                <span class="ml-2">{{
                  $t('message.pages.video.mutedIndicator.bgColor')
                }}</span>
              </v-row>
            </v-col>
          </v-row>

          <!-- personalized -->
          <v-row v-if="mutedIndicatorAnimation === 'custom'">
            <v-col cols="12">
              <div class="d-flex flex-wrap">
                <label for="v-file-input" class="konva__card">
                  <v-card class="konva__card--input">
                    <template v-if="!loadingButtonSave">
                      <v-file-input
                        :disabled="loadingButtonSave"
                        id="v-file-input"
                        hide-input
                        prepend-icon="mdi-cloud-upload-outline"
                        accept="image/png, image/jpeg, image/jpg, image/gif"
                        @change="handleUpload"
                      />
                      <span class="konva__card--span">
                        {{ $t('message.pages.video.mutedIndicator.fileInput') }}
                      </span>
                    </template>
                    <v-progress-circular
                      v-else
                      :size="22"
                      width="3"
                      color="primary"
                      indeterminate
                    />
                  </v-card>
                </label>

                <template v-if="konvaConfig.lib.length">
                  <v-hover
                    v-for="(item, index) in konvaConfig.lib"
                    v-slot="{ hover }"
                    :key="index"
                  >
                    <v-card
                      :class="{
                        'on-hover': hover,
                        disabled: loadingButtonSave,
                      }"
                      class="konva__card"
                      @click.stop="handleCustomSelect(item)"
                    >
                      <v-img
                        :src="item.metadata.imgURL"
                        width="150"
                        height="85"
                      />
                      <div class="align-self-center">
                        <v-btn
                          filter
                          icon
                          class="btn-handle-delete"
                          :class="{ 'show-btn-handle-delete': hover }"
                          @click.stop="handleCustomDelete({ item, open: true })"
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </v-card>
                  </v-hover>
                </template>
              </div>
            </v-col>
            <v-col cols="12">
              <p class="ma-0">
                <em>
                  {{ $t('message.pages.video.mutedIndicator.alertOptions') }}
                  <span
                    v-html="$t('message.pages.video.mutedIndicator.readMore')"
                  />
                </em>
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-scroll-x-transition>
    </v-row>
    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addAutoplay') }}
    </span>
    <v-row no-gutters v-if="hasEditVideoPerm">
      <v-col cols="12">
        <v-card-actions class="px-0 pt-4 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            class="mr-2 px-4"
            text
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUndoChanges"
          >
            {{ $t('message.pages.video.discardChanges') }}
          </v-btn>
          <v-btn
            color="primary"
            class="px-6"
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUpdateAutoplay"
            :loading="publishingChanges"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>

    <!-- dialog -->
    <unsaved-changes-modal />
    <muted-custom-delete
      v-model="deletingMutedCustom"
      @removing-current="handleRemovingCurrentCustom"
      @close="handleCloseMutedDelete"
    />
  </v-card>
</template>

<script>
// external imports
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'

// internal imports
import i18n from '@/translations/lang'
import colorHelpers from '@/utils/colorHelpers.js'

// API
import imagesAPI from '@/services/imagesApi.js'
import personalizedAPI from '@/services/personalizedApi.js'

// Components
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'

export default {
  name: 'VideoPlayerAutoplay',
  components: {
    UnsavedChangesModal,
    MutedCustomDelete: () => import('./MutedCustomDelete.vue'),
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
  },
  inject: ['konva'],
  data: () => ({
    videoId: '',
    loadingButtonSave: false,
    profileId: null,
    profileConfig: {},
    initialConfig: {},
    autoplay: false,
    smartAutoplay: false,
    mutedIndicatorIcon: false,
    mutedIndicatorAnimation: '',
    mutedIndicatorLoop: false,
    mutedIndicatorLoopDuration: 15,
    mutedIndicatorSize: 'small',
    mutedIndicatorBorderSize: 2,
    mutedIndicatorFontSize: 22,
    mutedIndicatorTextColor: '#FFFFFFFF',
    mutedIndicatorBackgroundColor: '#FF3FFFB3',
    mutedIndicatorTextBottom: '',
    mutedIndicatorTextTop: '',
    smallMutedIcon: undefined,
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        v.length <= 30 || i18n.t('message.maximumLengthConfigs'),
    },
    opacity: {
      max: (v) => v <= 100,
      min: (v) => v >= 0,
    },
    animation: {
      max: (v) => v <= 100,
      min: (v) => v >= 5,
    },
    withoutChanges: true,
    mutedFontColor: false,
    mutedBackgroundColor: false,
    isBeta: false,
    deletingMutedCustom: {
      open: false,
      item: null,
    },
    publishingChanges: false,
  }),
  watch: {
    '$store.state.video.profile': {
      handler() {
        this.withoutChanges = this.checkChanges()
      },
      deep: true,
    },
  },
  async mounted() {
    this.isBeta = location.search.match('beta')
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config.profile
      ? this.$store.state.video.video.config?.profile
      : {}
    this.profileConfig = this.$store.state.video.profile?.vsl
      ? this.$store.state.video.profile?.vsl
      : {}
    this.autoplay = this.$store.state.video.profile.vsl.autoplay
      ? this.$store.state.video.profile.vsl.autoplay
      : false
    this.smartAutoplay =
      this.$store.state.video.profile?.vsl.smartAutoplay || false
    this.mutedIndicatorFontSize =
      this.$store.state.video.profile?.vsl.mutedIndicatorFontSize || 22
    this.mutedIndicatorIcon =
      this.$store.state.video.profile?.vsl.mutedIndicatorIcon
    this.mutedIndicatorTextBottom =
      this.$store.state.video.profile?.vsl.mutedIndicatorTextBottom
    this.mutedIndicatorTextTop =
      this.$store.state.video.profile?.vsl.mutedIndicatorTextTop
    this.mutedIndicatorAnimation =
      this.$store.state.video.profile?.vsl.mutedIndicatorAnimation
    this.konvaConfig.oldMutedValue = this.mutedIndicatorAnimation
    this.konvaConfig.id =
      this.$store.state.video.profile?.vsl.customMutedIndicatorId
    this.mutedIndicatorLoop =
      this.$store.state.video.profile?.vsl.mutedIndicatorLoop
    this.mutedIndicatorLoopDuration =
      this.$store.state.video.profile?.vsl.mutedIndicatorLoopDuration
    this.mutedIndicatorSize =
      this.$store.state.video.profile?.vsl.mutedIndicatorSize
    this.mutedIndicatorBorderSize =
      this.$store.state.video.profile?.vsl.mutedIndicatorBorderSize
    this.mutedIndicatorBackgroundColor = this.$store.state.video.profile?.vsl
      .mutedIndicatorBackgroundColor
      ? colorHelpers.rgbaToHex(
          this.$store.state.video.profile?.vsl.mutedIndicatorBackgroundColor
        )
      : this.$store.state.video.profile.colors.primaryColor + 'B3'
    this.mutedIndicatorTextColor = this.$store.state.video.profile?.vsl
      .mutedIndicatorTextColor
      ? colorHelpers.rgbaToHex(
          this.$store.state.video.profile?.vsl.mutedIndicatorTextColor
        )
      : this.$store.state.video.profile.colors.controlsColor + 'FF'
    this.smallMutedIcon =
      this.$store.state.video.profile?.vsl.smallMutedIcon ??
      (this.autoplay && !this.mutedIndicatorIcon)
    this.initialConfig = { ...this.profile.vsl }
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })

    this.setMutedCustom({})
    this.loadCustomTemplates()
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    svgAnimationTypes() {
      return [
        {
          value: 'default',
          name: this.$t(
            'message.pages.video.mutedIndicator.animationsList.default'
          ),
        },
        {
          value: 'impact',
          name: this.$t(
            'message.pages.video.mutedIndicator.animationsList.impact'
          ),
        },
      ]
    },
    mutedIndicatorSizeTypes() {
      return [
        {
          name: i18n.t(
            'message.pages.video.mutedIndicator.mutedIndicatorSizeTypes.0'
          ),
          value: 'small',
        },
        {
          name: i18n.t(
            'message.pages.video.mutedIndicator.mutedIndicatorSizeTypes.1'
          ),
          value: 'medium',
        },
        {
          name: i18n.t(
            'message.pages.video.mutedIndicator.mutedIndicatorSizeTypes.2'
          ),
          value: 'large',
        },
      ]
    },
    setMutedFontColor() {
      return {
        backgroundColor: this.mutedIndicatorTextColor,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setMutedIndicatorBackgroundColor() {
      return {
        backgroundColor: this.mutedIndicatorBackgroundColor,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    konvaConfig() {
      return this.konva
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    setMutedCustom(data) {
      if (data.width) {
        this.$set(this.konvaConfig.img, 'width', data.width)
        this.$set(this.konvaConfig.img, 'height', data.height)
      } else {
        this.$delete(this.konvaConfig.img, 'width')
        this.$delete(this.konvaConfig.img, 'height')
      }

      this.konvaConfig.img.rotation = data.rotation
      this.konvaConfig.img.scaleX = data.scaleX
      this.konvaConfig.img.scaleY = data.scaleY
      this.konvaConfig.img.x = data.x
      this.konvaConfig.img.y = data.y

      // need to be the last, cuz parent component watch it
      this.konvaConfig.file = data.file || data.imgURL
    },
    async loadCustomTemplates() {
      this.loadingButtonSave = true

      try {
        const { data } = await personalizedAPI.getPersonalized()
        this.konvaConfig.lib = data
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingButtonSave = false
      }
    },
    handleTabDefault() {
      let value = this.konvaConfig.oldMutedValue

      if (this.konvaConfig.oldMutedValue === 'custom') value = 'default'

      this.mutedIndicatorAnimation = value
      this.setMutedCustom({})
    },
    handleTabCustom() {
      this.mutedIndicatorAnimation = 'custom'
      this.setMutedCustom({})
    },
    handleUpload(file) {
      this.konvaConfig.id = null
      this.konvaConfig.file = file
      this.profile.vsl.mutedIndicatorIcon = false
    },
    async handleCustomSelect({ id, metadata }) {
      this.konvaConfig.id = id

      this.setMutedCustom(metadata)

      // hide template from preview
      this.profile.vsl.mutedIndicatorIcon = false
    },
    async handleCustomDelete({ open, item }) {
      this.deletingMutedCustom.open = open
      this.deletingMutedCustom.item = item
    },
    handleCloseMutedDelete() {
      this.loadingButtonSave = false

      this.deletingMutedCustom.open = false
      this.deletingMutedCustom.item = null

      this.loadCustomTemplates()
    },
    async handleRemovingCurrentCustom() {
      this.loadingButtonSave = false

      this.deletingMutedCustom.open = false
      this.deletingMutedCustom.item = null

      this.konvaConfig.id = null

      this.setMutedCustom({})

      this.mutedIndicatorAnimation = 'default'
      this.mutedIndicatorIcon = false

      await this.handleUpdateAutoplay()
    },
    getCustomScript(metadata, src) {
      const { width, video_external_id } = this.video
      let script = ''
      const dStyle =
        'position:absolute;width:100%;height:100%;overflow:hidden; display: flex; justify-content: center;'

      const iStyle = ''

      script += `<div id="template_${video_external_id}" style="${dStyle}"><img src="${src}" style="${iStyle}"></div>`

      const innerHtml = `
          function isIOS() {
              return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
          }
          function resize_template(){
            let e=document.getElementById("template_${video_external_id}"),
              v=e.querySelector("img"),
              t=e.closest("html"),
              n=t.offsetWidth/${width},
              s="${iStyle}";
            if(isIOS()){
              v.style.transform = \`scale(1)\`;
            }else{
              v.style.transform = \`scale(\${n})\`;
            }
          }
          function checkElement(){
            document.getElementById("template_${video_external_id}"),
            clearInterval(intervalId),
            resize_template(),
            window.addEventListener("resize",resize_template,!1)
          }
          const intervalId=setInterval(checkElement,100);
          window.onload=function(){
            checkElement()
          };
        `

      /* eslint-disable no-useless-escape */
      script += /* HTML */ `<script>
        var scp = document.createElement('script')
        scp.innerHTML = ${innerHtml}
        document.head.appendChild(scp)
      <\/script>`

      return script
    },
    getCustomOptions(src, id) {
      // eslint-disable-next-line no-unused-vars
      const { name, image, draggable, ...metadata } = this.konvaConfig.img
      const { stage } = this.konvaConfig

      // needed to ...
      metadata.imgURL = src // reload template
      metadata.imgID = id // reload template
      metadata.stage = stage // calc dimension

      // gen options
      return {
        script: this.getCustomScript(metadata, src),
        metadata,
      }
    },
    async saveImage() {
      const blob = this.konvaConfig.file
      const options = [
        {
          filename: this.konvaConfig.file.name,
          type: 'template',
        },
      ]

      return await imagesAPI.handleUploadImage(blob, options)
    },
    async saveCustom(data) {
      const src = `${data.cdn_base}/${data.storage_path}`
      const options = this.getCustomOptions(src, data.id)

      try {
        const customData = await personalizedAPI.createPersonalized(options)
        this.konvaConfig.id = customData.data.id
      } catch (err) {
        console.error(err)
      }
    },
    async updateCustom() {
      const { id } = this.konvaConfig
      const item = _.find(this.konvaConfig.lib, { id })

      const options = this.getCustomOptions(
        this.konvaConfig.file,
        item.metadata.imgID
      )

      try {
        await personalizedAPI.updatePersonalized(id, options)
      } catch (err) {
        console.error(err)
      }
    },
    async handleMutedCustom() {
      const hasID = !!this.konvaConfig.id
      const hasFile = !!this.konvaConfig.file

      if (this.mutedIndicatorAnimation === 'custom') {
        // handle create
        if (!hasID && hasFile) {
          try {
            const imgData = await this.saveImage()
            await this.saveCustom(imgData)
          } catch (err) {
            console.error(err)
          }
        }

        // handle update
        else if (hasID && hasFile) {
          await this.updateCustom()
        }

        this.setMutedCustom({})
      }
    },
    async handleUpdateAutoplay() {
      this.loadingButtonSave = true
      this.publishingChanges = true

      await this.handleMutedCustom()

      const vsl = {
        ...this.profile.vsl,
        mutedIndicatorIcon: this.mutedIndicatorIcon,
        smartAutoplay: this.smartAutoplay,
        mutedIndicatorTextTop: this.mutedIndicatorTextTop,
        mutedIndicatorTextBottom: this.mutedIndicatorTextBottom,
        autoplay: this.autoplay,
        mutedIndicatorAnimation: this.mutedIndicatorAnimation,
        customMutedIndicatorId: this.konvaConfig.id,
        mutedIndicatorLoop: this.mutedIndicatorLoop,
        mutedIndicatorFontSize: this.mutedIndicatorFontSize,
        mutedIndicatorLoopDuration: this.mutedIndicatorLoopDuration,
        mutedIndicatorBackgroundColor: colorHelpers.hexToRgba(
          this.mutedIndicatorBackgroundColor
        ),
        mutedIndicatorTextColor: colorHelpers.hexToRgba(
          this.mutedIndicatorTextColor
        ),
        mutedIndicatorSize: this.mutedIndicatorSize,
        mutedIndicatorBorderSize: this.mutedIndicatorBorderSize,
        smallMutedIcon: this.smallMutedIcon,
      }

      if (
        this.mutedIndicatorTextBottom.length > 30 ||
        this.mutedIndicatorTextTop.length > 30
      ) {
        this.loadingButtonSave = false
        this.publishingChanges = false
        return
      }

      if (!this.video.config.profile) {
        await this.createProfile(this.videoId)
        this.video.config.profile = this.profile_id
        this.profileId = this.profile_id
        await this.setProfileToVideos([this.profileId, this.videoId])
      }

      const controls = this.$store.state.video.profile.controls
      const colors = this.$store.state.video.profile.colors
      try {
        await this.updateProfile([
          this.profileId,
          {
            vsl: vsl,
            colors: colors,
            controls: controls,
          },
        ])
        this.internalError
          ? ''
          : ((this.initialConfig = _.cloneDeep(this.profile.vsl)),
            this.handleSnackbar(this.$t('message.pages.video.changesSaved')),
            (this.withoutChanges = true))
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingButtonSave = false
        this.publishingChanges = false

        if (this.mutedIndicatorAnimation === 'custom') {
          this.konvaConfig.id =
            this.$store.state.video.profile?.vsl.customMutedIndicatorId
          this.konvaConfig.file = null

          await this.loadCustomTemplates()

          const iframe = document.getElementById('panda-player').contentWindow
          const item = _.find(this.konva.lib, { id: this.konva.id })
          const parameter = {
            script: item.script,
            metadata: item.metadata,
          }
          const run = () =>
            iframe.postMessage(
              { type: 'custom_muted_indicator', parameter },
              '*'
            )

          if (item) setTimeout(run, 1500)
        }
      }
      this.$root.$emit('updateEmbedPreviewChange')
      this.publishingChanges = false
    },
    changePlayer({ forceReset = false } = {}) {
      if (this.profile.vsl === null) {
        this.profile.vsl = {}
      }

      this.profile.vsl['autoplay'] = this.autoplay

      if (forceReset && this.autoplay === false) {
        this.smartAutoplay = false
        this.mutedIndicatorIcon = false
      }

      this.profile.vsl['mutedIndicatorIcon'] = this.mutedIndicatorIcon
      this.profile.vsl['mutedIndicatorFontSize'] = this.mutedIndicatorFontSize
      this.profile.vsl['smartAutoplay'] = this.smartAutoplay
      this.profile.vsl['mutedIndicatorTextBottom'] =
        this.mutedIndicatorTextBottom
      this.profile.vsl['mutedIndicatorTextTop'] = this.mutedIndicatorTextTop
      this.profile.vsl['mutedIndicatorAnimation'] = this.mutedIndicatorAnimation
      this.profile.vsl['customMutedIndicatorId'] = this.konvaConfig.id
      this.profile.vsl['mutedIndicatorLoop'] = this.mutedIndicatorLoop
      this.profile.vsl['mutedIndicatorLoopDuration'] =
        this.mutedIndicatorLoopDuration
      this.profile.vsl['mutedIndicatorTextColor'] = colorHelpers.hexToRgba(
        this.mutedIndicatorTextColor
      )
      this.profile.vsl['mutedIndicatorSize'] = this.mutedIndicatorSize
      this.profile.vsl['mutedIndicatorBorderSize'] =
        this.mutedIndicatorBorderSize
      this.profile.vsl['mutedIndicatorBackgroundColor'] =
        colorHelpers.hexToRgba(this.mutedIndicatorBackgroundColor)
      this.profile.vsl['smallMutedIcon'] = this.smallMutedIcon

      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)
      const iframe = document.getElementById('panda-player').contentWindow
      iframe.postMessage(
        {
          type: 'colors',
          parameter: {
            colors: {
              mutedIndicatorTextColor: colorHelpers.hexToRgba(
                this.mutedIndicatorTextColor
              ),
              mutedIndicatorBackgroundColor: colorHelpers.hexToRgba(
                this.mutedIndicatorBackgroundColor
              ),
            },
          },
        },
        '*'
      )
      this.$root.$emit('updateEmbedPreviewChange')
      if (forceReset && this.autoplay === false) {
        window.addEventListener('message', this.handleMessage)
      }
    },
    pausePlayer() {
      const iframe = document.getElementById('panda-player').contentWindow
      iframe.postMessage(
        {
          type: 'play_toggle',
        },
        '*'
      )
    },
    handleMessage(event) {
      const message = event.data.message

      if (message === 'panda_play') {
        this.pausePlayer()
      }
      if (message === 'panda_pause') {
        window.removeEventListener('message', this.handleMessage)
      }
    },
    handleUndoChanges() {
      this.autoplay = _.cloneDeep(this.initialConfig.autoplay)
      this.smartAutoplay = _.cloneDeep(this.initialConfig.smartAutoplay)
      this.mutedIndicatorIcon = _.cloneDeep(
        this.initialConfig.mutedIndicatorIcon
      )
      this.mutedIndicatorBackgroundColor = _.cloneDeep(
        colorHelpers.rgbaToHex(this.initialConfig.mutedIndicatorBackgroundColor)
      )
      this.mutedIndicatorTextColor = _.cloneDeep(
        colorHelpers.rgbaToHex(this.initialConfig.mutedIndicatorTextColor)
      )
      this.mutedIndicatorTextBottom = _.cloneDeep(
        this.initialConfig.mutedIndicatorTextBottom
      )
      this.mutedIndicatorTextTop = _.cloneDeep(
        this.initialConfig.mutedIndicatorTextTop
      )
      this.mutedIndicatorAnimation = _.cloneDeep(
        this.initialConfig.mutedIndicatorAnimation
      )
      this.mutedIndicatorLoop = _.cloneDeep(
        this.initialConfig.mutedIndicatorLoop
      )
      this.mutedIndicatorLoopDuration = _.cloneDeep(
        this.initialConfig.mutedIndicatorLoopDuration
      )
      this.mutedIndicatorSize = _.cloneDeep(
        this.initialConfig.mutedIndicatorSize
      )
      this.mutedIndicatorBorderSize = _.cloneDeep(
        this.initialConfig.mutedIndicatorBorderSize
      )
      this.mutedIndicatorFontSize = _.cloneDeep(
        this.initialConfig.mutedIndicatorFontSize
      )
      this.smallMutedIcon = _.cloneDeep(this.initialConfig.smallMutedIcon)
      this.konvaConfig.id = _.cloneDeep(
        this.initialConfig.customMutedIndicatorId
      )
      this.konvaConfig.file = null
      this.changePlayer()
    },
    checkChanges() {
      return _.isEqual(this.profile.vsl, this.initialConfig)
    },
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    numberOnly(evt) {
      const event = evt || window.event
      const expect = event.target.value.toString() + event.key.toString()

      if (!/^[0-9]*\.?[0-9]*$/.test(expect)) evt.preventDefault()
      else return true
    },
  },
}
</script>

<style lang="scss" scoped>
@mixin shadow($color, $size: 1px) {
  box-shadow: $size $size 2px $color, #{$size * -1} #{$size * -1} 2px $color !important;
}

.konva {
  &__card {
    margin: 0 1rem 1rem 0;

    display: flex;
    width: 150px;
    height: 85px;
    padding: 0;
    box-shadow: none !important;
    box-sizing: content-box;

    justify-content: center;
    align-items: center;

    &:not(label) {
      border: 1px solid var(--v-neutral-lighten3);
    }

    &:not([class*='--disabled']) {
      cursor: pointer;
    }

    &:hover:not([class*='--disabled']) {
      @include shadow(var(--v-neutral-lighten3));
    }

    &:active:not([class*='--disabled']) {
      @include shadow(var(--v-neutral-lighten2));
    }

    &.disabled {
      position: relative;
      pointer-events: none;
      overflow: hidden;

      &::after {
        content: '';

        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;

        display: block;
        width: 100%;
        height: 100%;
        background-color: rgba(#000, 0.35);
      }
    }

    &--span {
      position: absolute;
      top: 43px;

      font-size: 12px;
      text-align: center;
      color: var(--v-neutral-lighten1);
    }

    &--input {
      display: flex;
      width: 100%;
      height: 100%;
      border-style: dashed;
      border-color: var(--v-neutral-lighten2);
      box-shadow: none !important;

      justify-content: center;
      align-items: center;
    }

    &::v-deep {
      .v-input {
        margin: 0;

        display: flex;
        width: 100%;
        height: 100%;
        padding: 0;

        justify-content: center;
        align-items: center;

        &__prepend-outer {
          margin: 0 0 1rem;
        }
        .v-icon {
          color: var(--v-neutral-lighten1);
        }
      }
    }
  }

  &__iframe {
    border: none;
    overflow: hidden;
    pointer-events: none;
  }
}
.v-textfield {
  background-color: #ffffffb3;

  &::v-deep {
    input[type='number'] {
      -webkit-appearance: none;
      -moz-appearance: textfield;
      font-size: 1.125em;
    }
  }
}
</style>
